import React, { useState, useEffect } from 'react';
import { Box, Button, MenuItem, Select, TextField, Typography } from '@mui/material';
import DynamicTable from '../../components/Table/DataTable';
import TablePaginationActions from '../../components/Table/tablePagination';
import { IconCancel, IconEdit, IconEye } from '../../components/icons/icons';
import apiClient from '../../services/apiClient';
import moment from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { consultStatuses } from './AppointmentEditForm';

interface EmergencyConsultsProps {
    handleListViewClick: (row: any) => void;
    handleEditClick: (row: any, parentId: string) => void;
    fetchAppointments: () => void;
}

const EmergencyConsults: React.FC<EmergencyConsultsProps> = ({
    handleListViewClick,
    handleEditClick,
    fetchAppointments,
}) => {
    const [appointments, setAppointments] = useState<any[]>([]);
    const [filteredAppointments, setFilteredAppointments] = useState<any[]>([]);
    const [parentNames, setParentNames] = useState<{ [petId: string]: string }>({});
    const [parentId, setParentId] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [searchField, setSearchField] = useState<'searchQuery' | 'consultId' | 'participantRef' | 'consultStatus' | 'dateRange'>('searchQuery');
    const [consultStatus, setConsultStatus] = useState('');
    const [consultId, setConsultId] = useState('');
    const [participantRef, setParticipantRef] = useState('');
    const [fromDateValue, setFromDateValue] = useState<string | null>(null);
    const [toDateValue, setToDateValue] = useState<string | null>(null);

    // Fetch all appointments from backend
    const fetchAllAppointments = async (
        forcedValue?: string,
        forcedField?: typeof searchField
    ) => {
        try {
            setLoading(true);
            const value = forcedValue !== undefined ? forcedValue : searchValue;
            const field = forcedField || searchField;
            const params: any = {
                limit: 100,
                page: 1,
                order_by: 'id',
                dir: 1,
                is_emergency: true, // Always fetch only emergency consultations from backend
            };
            // Handle filters
            switch (field) {
                case 'searchQuery':
                    params.searchKey = value || '';
                    break;
                case 'consultId':
                    params.consult_id = value || '';
                    break;
                case 'participantRef':
                    params.participant_ref_number = value || '';
                    break;
                case 'consultStatus':
                    params.consult_status = value || '';
                    break;
                case 'dateRange':
                    if (fromDateValue) params.from_date = fromDateValue;
                    if (toDateValue) params.to_date = toDateValue;
                    break;
            }
            const queryString = new URLSearchParams(params).toString();
            const response: any = await apiClient(`v1/resource/consults?${queryString}`, { method: 'GET' });
            const emergencyConsults = response.data.consults || [];
            setAppointments(emergencyConsults);
            setFilteredAppointments(emergencyConsults);
            setTotalCount(emergencyConsults.length);
        } catch (err: any) {
            console.error("Error fetching appointments:", err);
            setError(err.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllAppointments();
    }, []);

    // Get paginated data for current page
    const getPaginatedData = () => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        console.log(`Pagination: showing items ${startIndex} to ${endIndex} of ${filteredAppointments.length}`);
        return filteredAppointments.slice(startIndex, endIndex);
    };

    const paginatedData = getPaginatedData();

    // Fetch parent info for each unique petId
    useEffect(() => {
        const fetchParentNames = async () => {
            const petIds = filteredAppointments
                .map((row: any) => row.participants?.find((p: any) => p.role === 'subscriber')?.ref_number)
                .filter((id: string | null | undefined): id is string => !!id && !parentNames[id]);
            const uniquePetIds = Array.from(new Set(petIds));
            for (const petId of uniquePetIds) {
                try {
                    const petRes: any = await apiClient(`v1/resource/pets?id=${petId}`, { method: 'GET' });
                    const pets = petRes?.data?.pets ?? [];
                    let primary_id: number | null = null;
                    if (pets.length > 0) {
                        const { primary_id: id } = pets[0];
                        primary_id = id;
                    }
                    setParentId(primary_id?.toString() || '');
                    const parentId = primary_id;
                    if (parentId) {
                        const patientRes: any = await apiClient(`v1/resource/patients?id=${parentId}`, {
                            method: 'GET',
                        });
                        const parent = patientRes?.data?.patients ?? [];
                        const user = parent[0]?.user;
                        const fullName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();
                        setParentNames(prev => ({ ...prev, [petId as string]: fullName || 'N/A' }));
                    }
                } catch (err) {
                    console.error(`Error fetching parent for petId ${petId}`, err);
                }
            }
        };
        if (filteredAppointments?.length > 0) fetchParentNames();
    }, [filteredAppointments]);

    const getRowStyle = (row: any) => {
        if (row?.reason === 'Emergency Consult' || row?.reason === 'Emergency consultation') {
            return {
                backgroundColor: '#FFE6E6',
            };
        }
        return {};
    };

    const getStatusClass = (slug?: string) => {
        if (!slug) return "bg-gray-500";
        const key = slug.toLowerCase();
        const statusMap: Record<string, string> = {
            new: "bg-blue-600",
            waiting: "bg-amber-500",
            unpaid: "bg-orange-500",
            ended: "bg-slate-600",
            started: "bg-indigo-600",
            inprogress: "bg-indigo-700",
            cancelled: "bg-red-600",
            "cancelled (you)": "bg-red-600",
            "cancelled (vet)": "bg-red-600",
            completed: "bg-green-600",
            confirmed: "bg-emerald-600",
            paid: "bg-teal-600",
            pending: "bg-amber-500",
            "payment pending": "bg-amber-600",
            accepted: "bg-purple-600",
            "vet accepted": "bg-purple-600",
            "vet joined": "bg-violet-600",
            "joined (you)": "bg-violet-700",
            "reschedule requested": "bg-cyan-500",
            rescheduled: "bg-cyan-700",
            "reschedule rejected": "bg-cyan-900",
            expired: "bg-red-500",
            "no show (you)": "bg-rose-500",
            "no show (vet)": "bg-rose-600",
            "refund pending": "bg-yellow-500",
            refunded: "bg-green-500",
            "refund failed": "bg-red-700",
            "refund initiated": "bg-teal-500",
            created: "bg-slate-500",
            "link ready": "bg-sky-500",
        };
        return statusMap[key] || "bg-gray-500";
    };

    const renderers = (row: any, index?: number) => {
        const Pet = row.participants?.find((p: any) => p.role === 'subscriber');
        const Vet = row.participants?.find((p: any) => p.role === 'publisher');
        const consultTypeMap: Record<string, string> = {
            virtual: "Teleconsult",
            home: "Home Visit",
            clinic: "Walk In",
        };
        const Type = consultTypeMap[row.consult_type] || row.consult_type;
        const petId = Pet?.ref_number;
        const parentName = petId ? parentNames[petId] : 'Loading...';
        return {
            serial: page * rowsPerPage + (index ?? 0) + 1,
            PetName: <Typography variant="caption">{Pet?.participant_info?.name}</Typography>,
            Vetenerian: <Typography variant="caption">{Vet?.participant_info?.name}</Typography>,
            DateTime: row?.scheduled_at
                ? moment(row.scheduled_at).format("DD-MM-YYYY hh:mm A")
                : "N/A",
            speciality: Vet?.participant_info?.additional_info?.consult_speciality ?? 'N/A',
            Parent: <Typography variant="caption">{parentName || 'N/A'}</Typography>,
            Type: <Typography variant="caption">{Type}</Typography>,
            // Reason: <Typography variant="caption">{row.reason || 'N/A'}</Typography>,
            status: (
                <span
                    className={`text-white text-xs px-2 py-1 rounded ${getStatusClass(
                        row.consult_current_status?.name
                    )}`}
                >
                    {row.consult_current_status?.name || '-'}
                </span>
            ),
        };
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const columns = [
        {
            key: 'serial',
            label: 'S.No',
            align: 'Left',
            render: (_row: any, index: number) => renderers(_row, index).serial,
        },
        { key: 'id', label: 'ID', align: 'Left' },
        { key: 'Vetenerian', label: 'Veterinarian', align: 'Left', render: (row: any) => renderers(row).Vetenerian },
        { key: 'Parent', label: 'Parent Name', align: 'Left', render: (row: any) => renderers(row).Parent },
        { key: 'PetName', label: 'Pet Name', align: 'Left', render: (row: any) => renderers(row).PetName },
        { key: 'Type', label: 'Type', align: 'center', render: (row: any) => renderers(row).Type },
        // { key: 'Reason', label: 'Reason', align: 'Left', render: (row: any) => renderers(row).Reason },
        { key: 'DateTime', label: 'Date & Time', align: 'Left', render: (row: any) => renderers(row).DateTime },
        { key: 'status', label: 'Status', align: 'Left', render: (row: any) => renderers(row).status },
    ];

    const actions = [
        {
            label: 'View',
            color: '#00bcd4',
            icon: <IconEye size={20} />,
            onClick: (row: any) => handleListViewClick(row),
        },
        {
            label: 'Edit',
            color: '#4caf50',
            icon: <IconEdit size={20} />,
            onClick: (row: any) => handleEditClick(row, parentId),
        },
        {
            label: 'Cancel',
            color: '#f44336',
            icon: <IconCancel size={20} />,
            onClick: async (row: any) => {
                const confirmCancel = window.confirm('Are you sure you want to cancel this action?');
                if (confirmCancel) {
                    try {
                        const response = await apiClient<any>(`v1/resource/consults/${row.id}`, {
                            method: 'PUT',
                            body: {
                                id: row?.id,
                                status: 'cancelled',
                            },
                        });
                        console.log('Status updated:', response);
                        fetchAllAppointments();
                        fetchAppointments(); // Also refresh parent component
                    } catch (error) {
                        console.error('Failed to update status:', error);
                        alert('Failed to cancel the appointment. Please try again.');
                    }
                }
            },
        },
    ];

    // Debug info
    useEffect(() => {
        console.log("Component state:");
        console.log("- Total appointments:", appointments.length);
        console.log("- Filtered emergency appointments:", filteredAppointments.length);
        console.log("- Current page:", page);
        console.log("- Rows per page:", rowsPerPage);
        console.log("- Paginated data count:", paginatedData.length);
    }, [appointments, filteredAppointments, page, rowsPerPage, paginatedData]);

    return (
        <div>
            {/* Debug info display */}
            {/* <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                    <strong>Debug Info:</strong> Showing {paginatedData.length} of {filteredAppointments.length} emergency consultations
                    (Total consults: {appointments.length})
                </p>
            </div> */}
            <div className="flex items-center gap-2 mb-2 mt-6 flex-wrap">
                <select
                    value={searchField}
                    onChange={(e) => {
                        setSearchField(e.target.value as any);
                        setSearchValue(''); // reset value when field changes
                        setFromDateValue(''); // reset dates
                        setToDateValue('');
                    }}
                    className="border px-2 py-1 rounded"
                >
                    <option value="searchQuery">Name / Mobile / Service</option>
                    <option value="consultId">Consult ID</option>
                    <option value="participantRef">Participant Ref</option>
                    <option value="consultStatus">Consult Status</option>
                    <option value="dateRange">Date Range</option>
                </select>
                {searchField === 'dateRange' ? (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="From Date"
                            value={fromDateValue ? new Date(fromDateValue) : null}
                            onChange={(date) => setFromDateValue(date ? date.toISOString().split('T')[0] : '')}
                            slotProps={{
                                textField: {
                                    className: "border px-2 py-1 rounded",
                                    size: "small",
                                }
                            }}
                        />
                        <DatePicker
                            label="To Date"
                            value={toDateValue ? new Date(toDateValue) : null}
                            onChange={(date) => setToDateValue(date ? date.toISOString().split('T')[0] : '')}
                            slotProps={{
                                textField: {
                                    className: "border px-2 py-1 rounded",
                                    size: "small",
                                }
                            }}
                        />
                    </LocalizationProvider>
                ) : searchField === 'consultStatus' ? (
                    <select
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="border px-2 py-1 rounded"
                    >
                        <option value="">All Status</option>
                        {consultStatuses.map((status) => (
                            <option key={status.value} value={status.value}>
                                {status.label}
                            </option>
                        ))}
                    </select>
                ) : (
                    <input
                        type="text"
                        placeholder={`Search by ${searchField}`}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="border px-2 py-1 rounded flex-1"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') fetchAllAppointments();
                        }}
                    />
                )}
                {/* Search button */}
                <button
                    onClick={() => fetchAllAppointments()}
                    className="bg-blue-600 text-white px-3 py-1 rounded"
                    disabled={
                        (searchField === 'dateRange' && (!fromDateValue || !toDateValue))
                    }
                >
                    Search
                </button>
                <button
                    onClick={() => {
                        // Reset all filter states
                        setSearchValue('');
                        setSearchField('searchQuery');
                        setConsultStatus('');
                        setConsultId('');
                        setParticipantRef('');
                        setFromDateValue(null);
                        setToDateValue(null);
                        // Reset pagination
                        setPage(0);
                        setRowsPerPage(10);
                        // Fetch all data without filters
                        fetchAllAppointments('', 'searchQuery');
                    }}
                    className="bg-red-400 text-white px-3 py-1 rounded"
                >
                    Clear
                </button>
            </div>
            <DynamicTable
                columns={columns}
                data={paginatedData}
                loading={loading}
                actions={actions}
                page={page}
                error={error}
                rowsPerPage={rowsPerPage}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                ActionsComponent={TablePaginationActions}
            // getRowStyle={getRowStyle}
            />
        </div>
    );
};

export default EmergencyConsults;